import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Gallery in carousel mode: `perView` images visible at a time, prev/next
 * arrows and page dots for the rest.
 *
 * Scroll-based (a snapping overflow container) rather than a transform track —
 * that gives native touch swipe on mobile, keyboard scrolling, and correct
 * behaviour in RTL for free. Arrows just scrollBy one page; the sign is taken
 * from the element's own computed direction, because scrollLeft runs negative
 * in RTL.
 *
 * Shared by BlockRenderer (canvas + preview), PublicCampaign and PublicTool so
 * the editor and the live page stay identical.
 */

export type ArrowIconName = 'chevron' | 'arrow' | 'triangle';

/** Arrow glyph, drawn pointing "start-ward" and mirrored for the end button
 *  (and again for RTL), so one path serves every direction. */
const ArrowGlyph: React.FC<{ icon: ArrowIconName; flip: boolean; size: number }> = ({ icon, flip, size }) => {
  const d = icon === 'triangle'
    ? 'M15 4 L7 12 L15 20 Z'
    : icon === 'arrow'
      ? 'M15 4 L7 12 L15 20 M7 12 H20'
      : 'M15 4 L7 12 L15 20';
  return (
    <svg
      viewBox="0 0 24 24"
      width={Math.round(size * 0.52)}
      height={Math.round(size * 0.52)}
      fill={icon === 'triangle' ? 'currentColor' : 'none'}
      stroke={icon === 'triangle' ? 'none' : 'currentColor'}
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: flip ? 'scaleX(-1)' : undefined, display: 'block' }}
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
};

export const GalleryCarousel: React.FC<{
  images: string[];
  /** How many images fit in one row/page. */
  perView?: number;
  /** Per-image column span (index-aligned with `images`); 1 = normal width. */
  spans?: number[];
  /** Per-image object-fit override (index-aligned); undefined = use `fit`. */
  fits?: (string | undefined)[];
  gap?: number;
  fit?: string;
  radius?: string;
  // ── arrow styling ──
  arrowColor?: string;
  arrowBg?: string;
  /** Button diameter in px. */
  arrowSize?: number;
  /** 0 = square … 50 = circle (percent of the button). */
  arrowRadius?: number;
  arrowShadow?: boolean;
  arrowIcon?: ArrowIconName;
  /** Dots are hidden when everything fits on one page. */
  showDots?: boolean;
  /** Static render for the builder canvas (no listeners, no interaction). */
  staticPreview?: boolean;
}> = ({
  images, perView = 3, spans, fits, gap = 8, fit = 'cover', radius = '6px',
  arrowColor = '#111827', arrowBg = 'rgba(255,255,255,0.92)', arrowSize = 34,
  arrowRadius = 50, arrowShadow = true, arrowIcon = 'chevron',
  showDots = true, staticPreview,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [rtl, setRtl] = useState(false);
  const n = images.length;
  const per = Math.max(1, Math.min(perView || 3, Math.max(1, n)));

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.clientWidth || 1;
    setPages(Math.max(1, Math.ceil(el.scrollWidth / w)));
    // scrollLeft is negative in RTL — magnitude is what matters.
    setPage(Math.round(Math.abs(el.scrollLeft) / w));
    setRtl(window.getComputedStyle(el).direction === 'rtl');
  }, []);

  useEffect(() => {
    if (staticPreview) return;
    measure();
    const el = trackRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, staticPreview, n, per, gap]);

  const scrollByPage = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const isRtl = window.getComputedStyle(el).direction === 'rtl';
    el.scrollBy({ left: el.clientWidth * dir * (isRtl ? -1 : 1), behavior: 'smooth' });
  };

  const canScroll = n > per;
  /** Width of one image, widened by its span (spans eat the gaps they cover). */
  const basisFor = (i: number) => {
    const span = Math.max(1, Math.min(per, Math.round(spans?.[i] || 1)));
    const one = `(100% - ${gap * (per - 1)}px) / ${per}`;
    return span === 1 ? `calc(${one})` : `calc(${one} * ${span} + ${gap * (span - 1)}px)`;
  };

  const size = Math.max(18, arrowSize || 34);
  const arrowStyle = (side: 'start' | 'end'): React.CSSProperties => ({
    position: 'absolute',
    [side === 'start' ? 'insetInlineStart' : 'insetInlineEnd']: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    width: size,
    height: size,
    borderRadius: `${Math.max(0, Math.min(50, arrowRadius))}%`,
    border: 'none',
    background: arrowBg,
    color: arrowColor,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: arrowShadow ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
    zIndex: 2,
    padding: 0,
  });

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        ref={trackRef}
        className="no-scrollbar"
        onScroll={staticPreview ? undefined : measure}
        style={{
          display: 'flex',
          gap,
          width: '100%',
          height: '100%',
          overflowX: staticPreview ? 'hidden' : 'auto',
          overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
        }}
      >
        {images.map((url, i) => (
          <div
            key={i}
            style={{
              flex: `0 0 ${basisFor(i)}`,
              height: '100%',
              minHeight: 0,
              borderRadius: radius,
              overflow: 'hidden',
              background: '#e5e7eb',
              scrollSnapAlign: 'start',
            }}
          >
            {url ? (
              <img src={url} alt="" style={{
                width: '100%', height: '100%',
                objectFit: (fits?.[i] || fit) as any, display: 'block',
              }} />
            ) : null}
          </div>
        ))}
      </div>

      {canScroll && (
        <>
          {/* Logical arrows: insetInlineStart/End place them correctly in RTL,
              and the glyph is mirrored to match the side it sits on. */}
          <button type="button" aria-label="הקודם" style={arrowStyle('start')}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); scrollByPage(-1); }}>
            <ArrowGlyph icon={arrowIcon} flip={rtl} size={size} />
          </button>
          <button type="button" aria-label="הבא" style={arrowStyle('end')}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); scrollByPage(1); }}>
            <ArrowGlyph icon={arrowIcon} flip={!rtl} size={size} />
          </button>
        </>
      )}

      {showDots && canScroll && pages > 1 && (
        <div style={{
          position: 'absolute', insetInline: 0, bottom: 6, display: 'flex',
          justifyContent: 'center', gap: 5, pointerEvents: 'none',
        }}>
          {Array.from({ length: pages }, (_, i) => (
            <span key={i} style={{
              width: i === page ? 16 : 6, height: 6, borderRadius: 999,
              background: i === page ? arrowColor : 'rgba(255,255,255,0.85)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'width .2s',
            }} />
          ))}
        </div>
      )}
    </div>
  );
};
